/**
 * @requirement S-043 AC #3 — "action registry" as the single
 *   source-of-truth for palette entries. Components register / clear
 *   actions as they mount; the palette reads the live snapshot on
 *   open.
 *
 * Module-scoped Set + subscriber pattern (small, no external
 * dependency). React consumers use `useActions()` to subscribe.
 */

import { useSyncExternalStore } from 'react';

import type { Action } from './types';

const REGISTRY = new Map<string, Action>();
const LISTENERS = new Set<() => void>();

function notify(): void {
  for (const listener of LISTENERS) listener();
}

export function registerAction(action: Action): () => void {
  REGISTRY.set(action.id, action);
  notify();
  return () => {
    REGISTRY.delete(action.id);
    notify();
  };
}

export function listActions(): readonly Action[] {
  return Array.from(REGISTRY.values());
}

let cachedSnapshot: readonly Action[] = [];

function getSnapshot(): readonly Action[] {
  // Cache the snapshot so React's `useSyncExternalStore` sees a
  // stable reference between renders — only allocate a new array
  // when the registry mutates (via `notify`).
  return cachedSnapshot;
}

function subscribe(listener: () => void): () => void {
  // Capture the wrapper closure so the cleanup can delete exactly
  // the caller's entry — not every listener. This preserves the
  // useSyncExternalStore contract when > 1 consumer subscribes
  // (e.g. tests mounting a probe alongside CommandPalette).
  const wrapped = (): void => {
    cachedSnapshot = Array.from(REGISTRY.values());
    listener();
  };
  LISTENERS.add(wrapped);
  return () => {
    LISTENERS.delete(wrapped);
  };
}

export function useActions(): readonly Action[] {
  // Initialise cached snapshot before first read.
  if (cachedSnapshot.length === 0 && REGISTRY.size > 0) {
    cachedSnapshot = Array.from(REGISTRY.values());
  }
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only reset. */
export function _resetRegistry(): void {
  REGISTRY.clear();
  LISTENERS.clear();
  cachedSnapshot = [];
}
